import { Card, CardContent, CardHeader } from '@/common/components/Card'
import { formatIsoDateToUsersTimezone } from '@/lib/utils'
import { DatabusMessageLog } from '@/queries/databus_message_logs/schema'
import { AppRoutePaths } from '@/routing/route_paths'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { ArrowLeft } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { getDatabusProcessingStatus, formatDatabusStatus } from './utils'
import { useHasPrivilege } from '@/hook/useHasPrivilege'
import NotAccessPage from '@/pages/not_access/NotAccessPage'

type LocationState = {
  messageLog?: DatabusMessageLog
}

function formatValue(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  return String(value)
}

export default function MessageQueueLogDetailsPage() {
  const location = useLocation()
  const { messageLog } = (location.state ?? {}) as LocationState

  const canReadMessageQueueLogs = useHasPrivilege('message_queue_logs_read')

  if (!canReadMessageQueueLogs) {
    return <NotAccessPage />
  }

  if (!messageLog) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Link
            to={AppRoutePaths.setup.message_queue_logs.index}
            className={buttonVariants({
              variant: 'outline',
              className: 'space-x-2',
            })}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Logs
          </Link>
        </div>

        <Card>
          <CardHeader className="text-lg font-semibold">Log Details</CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Log details are not available.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const rows = [
    ['Source system', messageLog.source_system],
    ['Entity', messageLog.entity],
    ['Entity ID (external identifier)', messageLog.entity_id],
    ['Action Performed', messageLog.action],
    [
      'Processing Status',
      formatDatabusStatus(getDatabusProcessingStatus(messageLog)),
    ],
    ['Error', messageLog.error],
    [
      'Processed Date',
      messageLog.processed_at
        ? formatIsoDateToUsersTimezone(messageLog.processed_at)
        : '-',
    ],
    [
      'Emitted Date',
      messageLog.event_emitted_at
        ? formatIsoDateToUsersTimezone(messageLog.event_emitted_at)
        : '-',
    ],
    ['Created At', formatIsoDateToUsersTimezone(messageLog.created_at)],
  ] as const

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Link
          to={AppRoutePaths.setup.message_queue_logs.index}
          className={buttonVariants({
            variant: 'outline',
            className: 'space-x-2',
          })}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Logs
        </Link>
      </div>

      <Card>
        <CardHeader className="text-lg font-semibold">Log Details</CardHeader>

        <CardContent className="p-0">
          <dl className="divide-y divide-gray-200">
            {rows.map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-1 gap-2 px-6 py-4 md:grid-cols-[14rem_1fr]"
              >
                <dt className="text-sm font-medium text-gray-500">{label}</dt>
                <dd className="whitespace-pre-wrap break-words text-sm text-gray-900">
                  {formatValue(value)}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}
